import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Container, 
  TextField, 
  InputAdornment,
  IconButton,
  Divider,
  Chip,
  Paper,
  Tooltip,
  Badge,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  Search as SearchIcon, 
  FilterList as FilterIcon,
  BookmarkBorder as BookmarkIcon,
  Bookmark as BookmarkedIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useLayoutContext } from '../context/LayoutContext';
import TemplatePreview from '../components/image-designer/TemplatePreview';

// API functions
const templatesAPI = {
  getAll: async () => {
    try {
      const response = await fetch('http://localhost:5000/api/carousel-templates');
      const data = await response.json();
      return data.success ? data.templates : [];
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  },
  
  delete: async (templateId) => {
    try {
      const response = await fetch(`http://localhost:5000/api/carousel-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error deleting template:', error);
      return false;
    }
  },
  
  incrementUsage: async (templateId) => {
    try {
      await fetch(`http://localhost:5000/api/carousel-templates/${templateId}/use`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error incrementing usage:', error);
    }
  }
};

const categories = [
  { id: 'all', name: 'All Templates' },
  { id: 'professional', name: 'Professional' },
  { id: 'creative', name: 'Creative' },
  { id: 'minimal', name: 'Minimal' },
  { id: 'corporate', name: 'Corporate' },
  { id: 'personal', name: 'Personal' }
];

const CarouselTemplates = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [allTemplates, setAllTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Load templates from API on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      try {
        const templates = await templatesAPI.getAll();
        setAllTemplates(templates);
      } catch (error) {
        console.error('Error loading templates:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadTemplates();
  }, []);

  // Filter templates based on search query and category
  useEffect(() => {
    let filtered = allTemplates;
    
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Filter by category (if not 'all')
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(template => 
        template.category?.toLowerCase() === selectedCategory.toLowerCase() ||
        template.tags?.some(tag => tag.toLowerCase() === selectedCategory.toLowerCase())
      );
    }
    
    setFilteredTemplates(filtered);
  }, [searchQuery, selectedCategory, allTemplates]);

  // Check if a specific template ID was provided in the URL
  useEffect(() => {
    if (templateId && allTemplates.length > 0) {
      const template = allTemplates.find(t => t.id === templateId);
      if (template) {
        setSelectedTemplate(template);
        // Increment usage count
        templatesAPI.incrementUsage(templateId);
      }
    }
  }, [templateId, allTemplates]);
  
  // If a template is selected via URL, navigate to the designer
  useEffect(() => {
    if (selectedTemplate) {
      navigate(`/carousel-designer/${selectedTemplate.id}`, { replace: true });
    }
  }, [selectedTemplate, navigate]);

  // Toggle favorite status
  const toggleFavorite = (templateId, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setFavorites(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Handle preview
  const handlePreview = (template, event) => {
    event.preventDefault();
    event.stopPropagation();
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  // Close preview
  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewTemplate(null);
  };

  // Create from scratch
  const handleCreateFromScratch = () => {
    navigate('/carousel-designer');
  };

  // Handle template deletion
  const handleDeleteTemplate = async (templateId) => {
    try {
      const success = await templatesAPI.delete(templateId);
      if (success) {
        // Reload templates
        const templates = await templatesAPI.getAll();
        setAllTemplates(templates);
        setDeleteConfirm(null);
        // Show success message (you might want to add a toast notification)
        console.log('Template deleted successfully');
      } else {
        console.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  // Handle template use
  const handleUseTemplate = (templateId) => {
    templatesAPI.incrementUsage(templateId);
    navigate(`/carousel-designer/${templateId}`);
  };

  return (
    <>
      <Helmet>
        <title>Carousel Templates | Instation</title>
        <meta name="description" content="Choose from our collection of professional carousel templates to create stunning LinkedIn posts." />
      </Helmet>
      
      <DashboardLayout>
        <Container maxWidth="xl" className="page-container">
          <Box className="content-container">
            <Paper 
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                mb: 3,
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              {/* Header */}
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2
              }}>
                <Typography 
                  variant="h6" 
                  className="component-heading"
                  sx={{ mb: 0 }}
                >
                  Carousel Templates
                </Typography>
                
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateFromScratch}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    bgcolor: 'var(--primary-color)',
                    '&:hover': { bgcolor: 'var(--primary-dark)' }
                  }}
                >
                  Create from Scratch
                </Button>
              </Box>

              {/* Search and Filter */}
              <Paper 
                elevation={0} 
                className="content-paper"
                sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2, 
                  flexWrap: 'wrap',
                  bgcolor: 'background.paper'
                }}
              >
                <TextField
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  sx={{ 
                    flexGrow: 1, 
                    minWidth: { xs: '100%', md: 300 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'var(--primary-color)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--primary-color)',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  flexWrap: 'wrap',
                  width: { xs: '100%', md: 'auto' }
                }}>
                  <IconButton size="small">
                    <FilterIcon fontSize="small" />
                  </IconButton>
                  <Divider orientation="vertical" flexItem />
                  {categories.map(category => (
                    <Chip 
                      key={category.id}
                      label={category.name}
                      onClick={() => setSelectedCategory(category.id)}
                      color={selectedCategory === category.id ? "primary" : "default"}
                      variant={selectedCategory === category.id ? "filled" : "outlined"}
                      sx={{ 
                        fontWeight: selectedCategory === category.id ? 500 : 400,
                        borderRadius: 2,
                        px: 0.75,
                        py: 0.5,
                        height: 32,
                        fontSize: '0.85rem',
                        '& .MuiChip-label': {
                          padding: '0 8px'
                        },
                        ...(selectedCategory === category.id ? {
                          bgcolor: 'var(--primary-color)',
                          color: 'white',
                          '&:hover': { 
                            bgcolor: 'var(--primary-dark)' 
                          }
                        } : {
                          '&:hover': { 
                            borderColor: 'var(--primary-color)',
                            color: 'var(--primary-color)'
                          }
                        })
                      }}
                    />
                  ))}
                </Box>
              </Paper>

              {/* Create From Scratch Card */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      border: '1px dashed rgba(0, 0, 0, 0.2)',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 15px rgba(0,0,0,0.08)',
                        borderColor: 'var(--primary-color)',
                      }
                    }}
                    onClick={handleCreateFromScratch}
                  >
                    <Box sx={{
                      position: 'relative',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                      cursor: 'pointer',
                      bgcolor: '#f8fafc',
                      aspectRatio: '1/1',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'rgba(72, 144, 254, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}>
                        <AddIcon sx={{ fontSize: 40, color: 'var(--primary-color)' }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'var(--primary-color)' }}>
                        Create from Scratch
                      </Typography>
                    </Box>
                    <CardContent sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      p: 2
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                        Start with a blank canvas and create your own carousel
                      </Typography>
                      <Box sx={{ mt: 'auto', pt: 2 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            py: 0.75,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            bgcolor: 'var(--primary-color)',
                            '&:hover': { bgcolor: 'var(--primary-dark)' }
                          }}
                        >
                          Start Creating
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Loading State */}
                {loading && Array.from({ length: 8 }).map((_, index) => (
                  <Grid item xs={12} sm={6} md={3} key={`skeleton-${index}`}>
                    <Card sx={{ height: 300, borderRadius: 2 }}>
                      <Box sx={{ height: 200, bgcolor: '#f5f5f5' }} />
                      <CardContent>
                        <Box sx={{ height: 20, bgcolor: '#f0f0f0', borderRadius: 1, mb: 1 }} />
                        <Box sx={{ height: 16, bgcolor: '#f0f0f0', borderRadius: 1, width: '60%' }} />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {/* Templates Grid */}
                {!loading && filteredTemplates.map((template) => (
                  <Grid item xs={12} sm={6} md={3} key={template.id}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: template.source === 'database' ? '1px solid rgba(72, 144, 254, 0.2)' : '1px solid rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 15px rgba(0,0,0,0.08)'
                        }
                      }}
                    >
                      <Link 
                        to={`/carousel-designer/${template.id}`} 
                        style={{ textDecoration: 'none' }}
                      >
                        <Box sx={{ 
                          position: 'relative',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                          cursor: 'pointer',
                          bgcolor: '#f5f5f5',
                          aspectRatio: '1/1',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <TemplatePreview 
                              template={template}
                              width="100%"
                            />
                          </Box>
                        </Box>
                        
                        {/* Custom template badge */}
                        {template.source === 'database' && (
                          <Chip
                            label="Custom"
                          size="small"
                          sx={{ 
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              bgcolor: 'var(--primary-color)',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 24
                            }}
                          />
                        )}

                        {/* Action buttons */}
                        <Box sx={{
                            position: 'absolute', 
                            top: 8, 
                            right: 8, 
                          display: 'flex',
                          gap: 0.5
                        }}>
                          {/* Delete button (only for editable templates) */}
                          {template.isEditable && (
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.9)',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                '&:hover': { bgcolor: 'rgba(255,0,0,0.1)' }
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDeleteConfirm(template.id);
                              }}
                            >
                              <DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} />
                            </IconButton>
                          )}
                          
                          {/* Bookmark button */}
                          <IconButton 
                            size="small"
                            sx={{ 
                            bgcolor: 'rgba(255,255,255,0.9)',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                          }}
                          onClick={(e) => toggleFavorite(template.id, e)}
                        >
                          {favorites.includes(template.id) ? (
                            <BookmarkedIcon fontSize="small" sx={{ color: 'var(--primary-color)' }} />
                          ) : (
                            <BookmarkIcon fontSize="small" />
                          )}
                        </IconButton>
                        </Box>
                      </Link>
                      <CardContent sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        p: 2
                      }}>
                        <Typography 
                          variant="subtitle1" 
                          className="section-title"
                          style={{ fontWeight: 500, color: 'black' }}
                          gutterBottom
                        >
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {template.description || 'Professional LinkedIn carousel template'}
                        </Typography>
                        {template.source === 'database' && template.usageCount > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            Used {template.usageCount} times
                          </Typography>
                        )}
                        {template.tags && template.tags.length > 0 && (
                          <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {template.tags.slice(0, 2).map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ))}
                          </Box>
                        )}
                        <Box sx={{ mt: 'auto', pt: 2 }}>
                          <Stack direction="row" spacing={1}>
                            <Button 
                              variant="outlined" 
                              fullWidth
                              startIcon={<VisibilityIcon />}
                              onClick={(e) => handlePreview(template, e)}
                              sx={{ 
                                py: 0.75,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                borderColor: 'var(--primary-color)',
                                color: 'var(--primary-color)',
                                '&:hover': { 
                                  borderColor: 'var(--primary-dark)',
                                  bgcolor: 'rgba(72, 144, 254, 0.04)'
                                }
                              }}
                            >
                              Preview
                            </Button>
                            <Button 
                              variant="contained" 
                              fullWidth
                              onClick={() => handleUseTemplate(template.id)}
                              endIcon={<ArrowForwardIcon />}
                              sx={{ 
                                py: 0.75,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                bgcolor: 'var(--primary-color)',
                                '&:hover': { bgcolor: 'var(--primary-dark)' }
                              }}
                            >
                              Use
                            </Button>
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                
                {!loading && filteredTemplates.length === 0 && (
                  <Grid item xs={12}>
                  <Box sx={{ 
                    p: 4, 
                    width: '100%', 
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px dashed rgba(0, 0, 0, 0.1)',
                    mt: 2
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      {searchQuery || selectedCategory !== 'all' 
                        ? 'No templates found matching your criteria'
                        : 'No templates available'
                      }
                    </Typography>
                    {(searchQuery || selectedCategory !== 'all') && (
                    <Button 
                      variant="outlined" 
                      sx={{ 
                        mt: 2, 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: 'var(--primary-color)',
                        color: 'var(--primary-color)',
                        '&:hover': { 
                          borderColor: 'var(--primary-dark)',
                          bgcolor: 'rgba(72, 144, 254, 0.04)'
                        }
                      }}
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                    )}
                  </Box>
                  </Grid>
                )}
              </Grid>
              
              {/* Footer */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 2,
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                mt: 'auto'
              }}>
                <Typography variant="body2" color="text.secondary">
                  {loading ? 'Loading...' : `${filteredTemplates.length} templates available`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create custom templates using our designer!
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Container>

        {/* Preview Dialog */}
        <Dialog
          open={showPreview}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {previewTemplate?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                LinkedIn Carousel • Multiple Slides
              </Typography>
            </Box>
            <IconButton onClick={handleClosePreview} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            {previewTemplate && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#f5f5f5',
                borderRadius: 2,
                p: 2,
                aspectRatio: '1/1',
                overflow: 'hidden'
              }}>
                <Box sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TemplatePreview 
                    template={previewTemplate} 
                    width="100%"
                  />
                </Box>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            gap: 2
          }}>
            <Button 
              onClick={handleClosePreview}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Close
            </Button>
            <Button 
              variant="contained"
              component={Link}
              to={`/carousel-designer/${previewTemplate?.id}`}
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                bgcolor: 'var(--primary-color)',
                '&:hover': { bgcolor: 'var(--primary-dark)' }
              }}
            >
              Use This Template
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={Boolean(deleteConfirm)}
          onClose={() => setDeleteConfirm(null)}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delete Template
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ pb: 2 }}>
            <Typography>
              Are you sure you want to delete this template? This action cannot be undone.
            </Typography>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
            <Button 
              onClick={() => setDeleteConfirm(null)}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleDeleteTemplate(deleteConfirm)}
              variant="contained"
              color="error"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </DashboardLayout>
    </>
  );
};

export default CarouselTemplates; 